import axios from "axios";
import "./interceptors";

// Adapts the new API's camelCase shape back to the legacy snake_case shape
// the SongContest screens read — including the nested `media`/`users`
// objects the carousel cards (`songs/`, `songs-featured/`, `playback-music/`)
// need for thumbnail/title/partner display, not just the flat list fields.
// Keeps this wrapper layer as the ONLY place that knows about the new API's
// naming.
function toLegacySong(row) {
  return {
    id: row.id,
    featured: row.featured,
    is_active: row.isActive,
    votes: row.voteCount,
    created_at: row.createdAt,
    // Whether the APP actually lists this song, and why not. The admin list is
    // intentionally unfiltered while the app's filters on activation + author
    // visibility, so the two differ by design — these say so out loud instead
    // of leaving the curator to guess ("the songs aren't the same songs").
    visible_in_app: row.visibleInApp,
    hidden_reason: row.hiddenReason,
    // Read camelCase (matches `SongContest.jsx`'s `songsFeatured.data[0].updatedAt`
    // — the elapsed-voting-time clock), unlike every other field here.
    updatedAt: row.updatedAt,
    media: row.media
      ? {
          title: row.media.title,
          name: row.media.title,
          thumbnail_url: row.media.thumbnailUrl,
          url_complete: row.media.url,
        }
      : { title: null, name: null, thumbnail_url: null, url_complete: null },
    users: (row.users || []).map((u) => ({
      id: u.id,
      name: u.name,
      profile_image_url: u.profileImageUrl,
      city: u.city,
      state: u.state,
    })),
    // The two lists behind the votes modal ("Registered users who voted" /
    // "Unregistered users who voted"). The modal reads `.length` on both
    // unguarded, and this wrapper supplied NEITHER — so opening it threw on
    // `undefined.length` and the guest voters it was built to show were
    // unreachable (2026-07-15). Default to [] so a song with no votes, or an
    // older API without these fields, still renders the empty state.
    votesLogged: (row.votesLogged || []).map((u) => ({
      id: u.id,
      name: u.name,
      profile_image_url: u.profileImageUrl,
      city: u.city,
      state: u.state,
    })),
    // Guests have no account — the email they voted with IS the identity.
    votesNotLogged: row.votesNotLogged || [],
  };
}

export function getSongContestList(featured = false) {
  let url = `api/v1/admin/song-contest?featured=${featured}&limit=100`;

  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const data = (res.data.data || []).map(toLegacySong);
        dispatch({
          type: "GET_SONG_CONTEST_LIST",
          data: { data },
        });
        return { data };
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function addedFeaturedSongs(songsId) {
  let url = `api/v1/admin/song-contest/featured`;

  return (dispatch) => {
    return axios
      .post(url, { featured: true, songsId })
      .then((res) => {
        dispatch({
          type: "ADDED_FEATURED_SONGS",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function removedFeaturedSongs(songsId) {
  let url = `api/v1/admin/song-contest/featured`;

  return (dispatch) => {
    return axios
      .post(url, { featured: false, songsId })
      .then((res) => {
        dispatch({
          type: "REMOVED_FEATURED_SONGS",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function enableSong(id, is_active) {
  let url = `api/v1/admin/song-contest/active`;

  return (dispatch) => {
    return axios
      .post(url, { id, isActive: is_active })
      .then((res) => {
        dispatch({
          type: "ENABLED_SONG",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  };
}

export function deleteSongs(songsId) {
  let url = `api/v1/admin/song-contest/delete`;

  return (dispatch) => {
    return axios
      .post(url, { songsId })
      .then((res) => {
        dispatch({
          type: "DELETE_SONGS",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        throw err;
      });
  };
}
